import React, { MutableRefObject } from "react";
import { useNavigate } from "react-router-dom";
import { Cell, Column } from "react-table";
import { Text } from "@chakra-ui/react";
import { lowerCase, startCase } from "lodash";

import { Table, TableProps } from "@alpacahq/alpaca-component-library";

import {
  AcatsDtccTransferStatusDescriptions,
  AcatsDtccTransferType,
  AcatsDtccTransferTypeDescriptions,
  AcatsStatusDetails,
  AcatsTransfer,
} from "../../api/acats/acats";
import UUID from "../../components/general/UUID";
import { formatDate } from "../../globals/utils";

export const DEFAULT_PAGE_SIZE = 100;

interface AcatsTransfersTableProps {
  data: AcatsTransfer[];
  isLoading: boolean;
  onFetchData: TableProps<AcatsTransfer>["onFetchData"];
  skipPageResetRef: MutableRefObject<boolean>;
}

const AcatsTransfersTable = ({
  data,
  isLoading,
  onFetchData,
  skipPageResetRef,
}: AcatsTransfersTableProps): JSX.Element => {
  const navigate = useNavigate();
  const columns: Column<AcatsTransfer>[] = [
    {
      id: "acats_id",
      accessor: "acats_id",
      Header: "ACATS ID",
      Cell: ({
        row: {
          original: { acats_id, account_id },
        },
      }: Cell<AcatsTransfer>) => (
        <UUID
          value={acats_id}
          onClick={() => navigate(`/acats/transfers/${account_id}/${acats_id}`)}
        />
      ),
    },
    {
      id: "transfer_identifier",
      accessor: "transfer_identifier",
      Header: "Control #",
      Cell: ({
        row: {
          original: { acats_id, account_id, transfer_identifier },
        },
      }: Cell<AcatsTransfer>) => (
        <UUID
          disableCopy={!transfer_identifier}
          value={transfer_identifier ?? "View"}
          onClick={() => navigate(`/acats/transfers/${account_id}/${acats_id}`)}
        />
      ),
    },
    {
      id: "account_number",
      accessor: "account_number",
      Header: "Account #",
      Cell: ({
        row: {
          original: { account_id, account_number },
        },
      }: Cell<AcatsTransfer>) => (
        <UUID
          value={account_number}
          onClick={() => navigate(`/accounts/${account_id}`)}
        />
      ),
    },
    {
      accessor: "contra_account_number",
      Header: "Contra Account #",
    },
    {
      accessor: "contra_broker_number",
      Header: "Contra DTC #",
    },
    {
      id: "transfer_type",
      accessor: "transfer_type",
      Header: "Type",
      Cell: ({ value }: Cell<AcatsTransfer, AcatsDtccTransferType>) => (
        <Text>{AcatsDtccTransferTypeDescriptions[value]}</Text>
      ),
    },
    {
      id: "dtcc_status",
      accessor: "status",
      Header: "DTC Status",
      Cell: ({ value: status }: Cell<AcatsTransfer, AcatsStatusDetails>) => (
        <Text>
          {status.dtcc_status
            ? AcatsDtccTransferStatusDescriptions[status.dtcc_status]
            : "-"}
        </Text>
      ),
    },
    {
      id: "transfer_status",
      accessor: "status",
      Header: "Status",
      Cell: ({ value: status }: Cell<AcatsTransfer, AcatsStatusDetails>) => (
        <Text>{startCase(lowerCase(status.transfer_status))}</Text>
      ),
    },
    {
      id: "created_at",
      accessor: "created_at",
      Header: "Created At",
      Cell: ({ value }: Cell<AcatsTransfer>) => (
        <Text>{formatDate(value)}</Text>
      ),
    },
    {
      id: "settlement_date",
      accessor: "settlement_date",
      Header: "Settlement Date",
      Cell: ({ value }: Cell<AcatsTransfer>) => (
        <Text>{formatDate(value)}</Text>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      data={data}
      defaultPerPage={DEFAULT_PAGE_SIZE}
      isLoading={isLoading}
      pagination
      paginationPosition="top"
      pageCount={-1}
      onFetchData={onFetchData}
      skipPageResetRef={skipPageResetRef}
    />
  );
};

export default AcatsTransfersTable;
